import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket debe usarse dentro de un SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectAttempts = useRef(0);
  const maxSilentReconnects = 5; // Después de 5 intentos, deja de mostrar errores

  useEffect(() => {
    // Conectar al servidor de producción
    const newSocket = io('https://truebavirtual.onrender.com', {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity, // Intentar reconectar indefinidamente
      timeout: 20000
    });

    newSocket.on('connect', () => {
      console.log('✅ Conectado a Socket.IO en tiempo real');
      setIsConnected(true);
      reconnectAttempts.current = 0; // Reset contador
    });

    newSocket.on('disconnect', (reason) => {
      console.log('⚠️ Desconectado de Socket.IO:', reason);
      setIsConnected(false);

      if (reason === 'io server disconnect') {
        // El servidor forzó la desconexión, reconectar manualmente
        newSocket.connect();
      }
    });

    newSocket.on('connect_error', () => {
      reconnectAttempts.current++;

      // Solo mostrar errores en los primeros intentos o cada 10 intentos
      if (reconnectAttempts.current <= maxSilentReconnects || reconnectAttempts.current % 10 === 0) {
        console.warn(
          `⚠️ No se pudo conectar a Socket.IO (intento ${reconnectAttempts.current}). ` +
          'Las actualizaciones en tiempo real no están disponibles. ' +
          'Esto es normal si el servidor está apagado o iniciándose.'
        );
      }

      setIsConnected(false);
    });

    newSocket.on('reconnect_attempt', (attemptNumber) => {
      if (attemptNumber <= 3) {
        console.log(`🔄 Intentando reconectar... (${attemptNumber})`);
      }
    });

    newSocket.on('reconnect', (attemptNumber) => {
      console.log(`✅ Reconectado después de ${attemptNumber} intentos`);
      setIsConnected(true);
      reconnectAttempts.current = 0;
    });

    newSocket.on('reconnect_failed', () => {
      console.error('❌ No se pudo reconectar después de múltiples intentos');
    });

    setSocket(newSocket);

    // Cleanup al desmontar
    return () => {
      console.log('🔌 Cerrando conexión Socket.IO');
      newSocket.close();
    };
  }, []);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};
